import { ProductLineItem } from '../types';
import type { CartApiResponse, CartStateData, ShopifyCartJson } from '../types';
import { formatCartState } from './formatCartState';
import appStore from './state';
import { markPendingPostEnterCartRefresh, syncFreeGift } from './syncFreeGift';

async function fetchCartJson(): Promise<ShopifyCartJson> {
  // @ts-expect-error Shopify is injected on the storefront
  const response = await fetch(`${window.Shopify.routes.root}cart.js`);
  if (!response.ok) throw new Error(`Cart fetch failed (${response.status})`);
  return response.json() as Promise<ShopifyCartJson>;
}

function wouldMixGiftcardAndProduct(lineItems: ProductLineItem[], cart: ShopifyCartJson): boolean {
  const addingGiftCard = lineItems.some((item) => item.gift_card === true);
  const addingProduct = lineItems.some((item) => item.gift_card !== true);

  if (addingGiftCard && addingProduct) {
    return true;
  }

  const cartHasGiftCard = cart.items.some((item) => item.gift_card === true);
  const cartHasProduct = cart.items.some((item) => item.gift_card !== true);

  return (cartHasGiftCard && addingProduct) || (cartHasProduct && addingGiftCard);
}

function toCartAddPayload(lineItems: ProductLineItem[]): Array<{
  id: string;
  quantity: number;
  properties?: Record<string, string>;
}> {
  return lineItems.map(({ id, quantity, properties }) => {
    const item: { id: string; quantity: number; properties?: Record<string, string> } = { id, quantity };
    if (properties) {
      item.properties = properties;
    }
    return item;
  });
}

export const addToCart = async (lineItem: ProductLineItem | ProductLineItem[]): Promise<CartStateData | undefined> => {
  const lineItems = Array.isArray(lineItem) ? lineItem : [lineItem];

  appStore.actions.closeGiftboxModal();
  appStore.actions.closeShopTheLookModal();

  try {
    const cart = await fetchCartJson();
    if (wouldMixGiftcardAndProduct(lineItems, cart)) {
      appStore.actions.openGiftcardWarningModal();
      return undefined;
    }
  } catch (error) {
    console.error(error);
    return undefined;
  }

  appStore.actions.setCartUpdating(true);

  try {
    // @ts-expect-error Shopify is injected on the storefront
    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items: toCartAddPayload(lineItems), sections: 'cart-drawer,giftbox-modal' }),
    });

    if (!response.ok) {
      throw new Error(`Cart change failed (${response.status})`);
    }

    const data = (await response.json()) as CartApiResponse;
    const synced = (await syncFreeGift(data)) ?? data;

    const cartData: CartStateData = formatCartState(synced);

    // Drawer already open: openCartModal is a no-op, so open-path refresh won't run.
    // Mark a one-shot refresh after cart-item enter anims so Free labels can catch up.
    if (appStore.getState().isCartModalOpen) {
      markPendingPostEnterCartRefresh();
    }

    appStore.actions.setCart(cartData);
    appStore.actions.openCartModal();

    return cartData;
  } catch (error) {
    console.error(error);
    return undefined;
  } finally {
    appStore.actions.setCartUpdating(false);
  }
};
